<form [formGroup]="formGroup">
  <div class="space-y-12">
    <div class="border-b border-gray-900/10 pb-12">
      <h2 class="primary-sub-title">Error Code: {{ item?.code }}</h2>
      <p class="secondary-small-text mb-2">{{ item?.nwNumbersString }}</p>
      <p class="mt-1 text-sm/6 text-gray-600">You can edit the error code here.</p>

      <div class="mt-6 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
        <div class="col-span-4">
          <app-label-with-help for="code" class="primary-label" fieldConfigurationAddress="errorcode.code"
            >Code</app-label-with-help
          >
          <div class="relative">
            <input
              type="text"
              id="code"
              class="primary-field"
              [placeholder]="errorCodeCodePlaceholder"
              required
              formControlName="code"
              [readOnly]="basicEditDisabled"
              appTrackUnsavedField
              [reset$]="resetFieldTracking$"
            />
          </div>

          <div class="col-span-full pt-4">
            <app-label-with-help for="nwNumbers" class="primary-label" fieldConfigurationAddress="errorcode.nwNumbers"
              >NW Numbers</app-label-with-help
            >
            <div class="relative">
              <app-multi-selection
                formControlName="nwNumbers"
                [options]="nwNumberValues"
                appTrackUnsavedField
                [reset$]="resetFieldTracking$"
                (unsavedChanges)="hasNwNumbersListUnsavedChanges = $event"
                [unsavedChanges]="hasNwNumbersListUnsavedChanges"
                [fullWidth]="true"
              >
              </app-multi-selection>
            </div>
          </div>
        </div>

        <div class="col-span-full">
          <app-text-area
            [fieldConfigurationAddress]="'errorcode.startingWithVersion'"
            [label]="'Available starting with version'"
            [isTranslatable]="false"
            formControlName="startingWithVersion"
            [readonly]="basicEditDisabled || elevatedEditDisabled || updateTranslationFieldDisabled"
            [reset$]="resetFieldTracking$"
            [rows]="1"
          ></app-text-area>
        </div>

        <div class="col-span-full">
          <app-text-area
            [fieldConfigurationAddress]="'errorcode.endingWithVersion'"
            [label]="'Last version available in'"
            [isTranslatable]="false"
            formControlName="endingWithVersion"
            [readonly]="basicEditDisabled || elevatedEditDisabled || updateTranslationFieldDisabled"
            [reset$]="resetFieldTracking$"
            [rows]="1"
          ></app-text-area>
        </div>

        <div class="col-span-full">
          <app-text-area
            [fieldConfigurationAddress]="'errorcode.MachineType'"
            [label]="'Machine Type(s)'"
            formControlName="machineType"
            [readonly]="basicEditDisabled"
            [reset$]="resetFieldTracking$"
          ></app-text-area>
        </div>

        <div class="col-span-full">
          <app-label-with-help for="category" class="primary-label" fieldConfigurationAddress="errorcode.category"
            >Category</app-label-with-help
          >
          <div class="relative">
            <app-selection
              formControlName="category"
              [options]="errorCodeCategories"
              [invalidOption]="errorCodeCategories[0].id"
              appTrackUnsavedField
              [reset$]="resetFieldTracking$"
              (unsavedChanges)="hasCategoryUnsavedChanges = $event"
              [unsavedChanges]="hasCategoryUnsavedChanges"
            ></app-selection>
          </div>
        </div>

        <div class="flex col-span-full gap-x-4">
          <div class="flex items-start relative">
            <div class="flex items-center h-5">
              <input
                type="checkbox"
                formControlName="needsReview"
                name="needsReview"
                id="needsReview"
                class="primary-checkbox"
                appTrackUnsavedField
                [reset$]="resetFieldTracking$"
              />
            </div>
            <app-label-with-help
              for="needsReview"
              class="ms-2 primary-label"
              fieldConfigurationAddress="errorcode.needsReview"
              >Needs Review</app-label-with-help
            >
          </div>
          <div class="flex items-start relative">
            <div class="flex items-center h-5">
              <input
                type="checkbox"
                formControlName="fitnessExclude"
                name="fitnessExclude"
                id="fitnessExclude"
                class="primary-checkbox"
                appTrackUnsavedField
                [reset$]="resetFieldTracking$"
              />
            </div>
            <app-label-with-help
              for="fitnessExclude"
              class="primary-label ms-2"
              fieldConfigurationAddress="errorcode.fitnessExclude"
              >Fitness Exclude</app-label-with-help
            >
          </div>
          <div class="flex items-start relative">
            <div class="flex items-center h-5">
              <input
                type="checkbox"
                formControlName="aggregateMessage"
                name="aggregateMessage"
                id="aggregateMessage"
                class="primary-checkbox"
                appTrackUnsavedField
                [reset$]="resetFieldTracking$"
              />
            </div>
            <app-label-with-help
              for="aggregateMessage"
              class="primary-label ms-2"
              fieldConfigurationAddress="errorcode.aggregateMessage"
              >Aggregate Message</app-label-with-help
            >
          </div>
        </div>

        <div class="col-span-full">
          <app-text-area
            [fieldConfigurationAddress]="'errorcode.message'"
            [label]="'Message'"
            [isTranslatable]="isNotDefaultLanguage"
            formControlName="message"
            [readonly]="basicEditDisabled || elevatedEditDisabled || updateTranslationFieldDisabled"
            [reset$]="resetFieldTracking$"
          ></app-text-area>
        </div>

        <div class="col-span-full">
          <app-text-area
            [warning]="translatableFieldsDocumentationWarning"
            [fieldConfigurationAddress]="'errorcode.causeFarmer'"
            [label]="'Cause (Farmer)'"
            [isTranslatable]="isNotDefaultLanguage"
            formControlName="causeFarmer"
            [readonly]="basicEditDisabled || elevatedEditDisabled || updateTranslationFieldDisabled"
            [reset$]="resetFieldTracking$"
          ></app-text-area>
        </div>

        <div class="col-span-full">
          <app-text-area
            [warning]="translatableFieldsDocumentationWarning"
            [fieldConfigurationAddress]="'errorcode.remedyFarmer'"
            [label]="'Remedy (Farmer)'"
            [isTranslatable]="isNotDefaultLanguage"
            formControlName="remedyFarmer"
            [readonly]="basicEditDisabled || elevatedEditDisabled || updateTranslationFieldDisabled"
            [reset$]="resetFieldTracking$"
          ></app-text-area>
        </div>

        <div class="col-span-full">
          <app-text-area
            [fieldConfigurationAddress]="'errorcode.causeDealer'"
            [label]="'Cause (Dealer)'"
            [isTranslatable]="isNotDefaultLanguage"
            formControlName="causeDealer"
            [readonly]="basicEditDisabled || elevatedEditDisabled || updateTranslationFieldDisabled"
            [reset$]="resetFieldTracking$"
          ></app-text-area>
        </div>

        <div class="col-span-full">
          <app-text-area
            [fieldConfigurationAddress]="'errorcode.remedyDealer'"
            [label]="'Remedy (Dealer)'"
            [isTranslatable]="isNotDefaultLanguage"
            formControlName="remedyDealer"
            [readonly]="basicEditDisabled || elevatedEditDisabled || updateTranslationFieldDisabled"
            [reset$]="resetFieldTracking$"
          ></app-text-area>
        </div>

        <div class="col-span-full">
          <app-text-area
            [fieldConfigurationAddress]="'errorcode.causeIntern'"
            [label]="'Cause (Intern)'"
            [isTranslatable]="isNotDefaultLanguage"
            formControlName="causeIntern"
            [readonly]="basicEditDisabled || elevatedEditDisabled || updateTranslationFieldDisabled"
            [reset$]="resetFieldTracking$"
          ></app-text-area>
        </div>

        <div class="col-span-full">
          <app-text-area
            [fieldConfigurationAddress]="'errorcode.remedyIntern'"
            [label]="'Remedy (Intern)'"
            [isTranslatable]="isNotDefaultLanguage"
            formControlName="remedyIntern"
            [readonly]="basicEditDisabled || elevatedEditDisabled || updateTranslationFieldDisabled"
            [reset$]="resetFieldTracking$"
          ></app-text-area>
        </div>

        <div class="col-span-full border-t border-gray-900/10 pt-4">
          <app-text-area
            [fieldConfigurationAddress]="'errorcode.internalNotes'"
            [label]="'Internal notes'"
            formControlName="internalNotes"
            [fieldIsRequired]="false"
            [reset$]="resetFieldTracking$"
          ></app-text-area>
        </div>
      </div>
    </div>
  </div>

  <div class="mt-6 mb-4 flex items-center justify-end gap-x-6">
    <button
      [disabled]="formGroup.invalid"
      [ngClass]="{
        'cursor-not-allowed': formGroup.invalid,
        'opacity-25': formGroup.invalid,
      }"
      (click)="saveErrorCode()"
      type="button"
      class="crud-btn-sidebar"
      appAgentAction
      agent="edit-error-code-save"
      businessObject="errorCode"
      [businessId]="$safeNavigationMigration(item?.id)"
      action="save"
    >
      <app-floppy-icon></app-floppy-icon>
    </button>
  </div>
</form>
